import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useNotification } from '../../contexts/NotificationContext'
import { minoriApi, entriesApi, pepApi } from '../../services/api'
import { useFeature } from '../../hooks/useFeature'
import { PageLoader, Card, Badge } from '../../components/ui'
import { QuickTags, QuickNotes } from '../../components/ui/QuickTags'
import { ArrowLeft, Baby, Calendar, FileText, User, Plus, Edit, Clock, CheckCircle, Phone, MapPin, Users, Tag, StickyNote, Target, GitBranch, Download, ClipboardList } from 'lucide-react'

import ExperimentalWrapper from '../../components/pep/ExperimentalWrapper'
import VerificaForm from '../../components/pep/VerificaForm'
import ColloquioForm from '../../components/pep/ColloquioForm'
import NuovoObiettivoForm from '../../components/pep/NuovoObiettivoForm'
import PromoteEntryButton from '../../components/pep/PromoteEntryButton'
import PepImportRapido from './PepImportRapido'
import PepExportModal from './PepExportModal'
import PepChiusuraModal from './PepChiusuraModal'
import PepTab from './PepTab'
import TimelineTab from './TimelineTab'
import PepWizard from './PepWizard'

export default function MinoreDetailPage() {
  const { id } = useParams()
  const [loading, setLoading] = useState(true)
  const [minore, setMinore] = useState(null)
  const [entries, setEntries] = useState([])
  const { isCoordinatore, tenant } = useAuth()
  const { showError, showSuccess } = useNotification()
  const navigate = useNavigate()

  // PEP state
  const [pepEnabled, setPepEnabled] = useState(false)
  const [pep, setPep] = useState(null)
  const [pepLoading, setPepLoading] = useState(false)
  const [timeline, setTimeline] = useState(null)
  const [activeTab, setActiveTab] = useState('progetto')
  const [showPepWizard, setShowPepWizard] = useState(false)
  const [verificaTarget, setVerificaTarget] = useState(null) // obId per aprire VerificaForm
  const [showColloquioForm, setShowColloquioForm] = useState(false)
  const [showImportRapido, setShowImportRapido] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showChiusuraModal, setShowChiusuraModal] = useState(false)
  const [showNuovoObiettivo, setShowNuovoObiettivo] = useState(false)
  const [editObiettivoId, setEditObiettivoId] = useState(null) // obId per edit form

  // Il PEP e visibile solo se la feature sperimentale e abilitata per il tenant
  const isPepFeatureEnabled = tenant?.experimentalFeatures?.pep?.enabled === true

  // Prep Colloquio
  const prepColloquio = useFeature('prepColloquio')
  const [prepData, setPrepData] = useState(null)
  const [prepLoading, setPrepLoading] = useState(false)
  const [prepPeriodo, setPrepPeriodo] = useState(30)

  useEffect(() => {
    loadData()
  }, [id])

  useEffect(() => {
    if (isPepFeatureEnabled && id) {
      loadPepData()
    }
  }, [isPepFeatureEnabled, id])

  const loadData = async () => {
    try {
      setLoading(true)
      const [minoreRes, entriesRes] = await Promise.all([
        minoriApi.get(id),
        entriesApi.getByMinore(id, { limit: 10 })
      ])
      const minoreData = minoreRes.data?.data?.minore || minoreRes.data?.data || minoreRes.data
      setMinore(minoreData)
      const entriesData = entriesRes.data?.data?.entries || entriesRes.data?.data || entriesRes.data
      setEntries(Array.isArray(entriesData) ? entriesData : [])
    } catch (error) {
      showError('Errore nel caricamento')
      navigate('/minori')
    } finally {
      setLoading(false)
    }
  }

  const loadPepData = async () => {
    try {
      setPepLoading(true)
      const pepRes = await pepApi.getAttivo(id)
      const pepData = pepRes.data?.data || null
      setPep(pepData)
      setPepEnabled(true)

      // Carica timeline se PEP esiste
      if (pepData?._id) {
        const tlRes = await pepApi.getTimeline(pepData._id)
        setTimeline(tlRes.data?.data || null)
      }
    } catch (error) {
      // 403 = feature non abilitata, non mostrare le tab
      if (error.response?.status === 403) {
        setPepEnabled(false)
      }
      // Altre errori silenziosi (il PEP e opzionale)
    } finally {
      setPepLoading(false)
    }
  }

  // Carica dati Prep Colloquio
  const loadPrepColloquio = async (periodo = prepPeriodo) => {
    if (!prepColloquio.visible) return
    setPrepLoading(true)
    try {
      const res = await minoriApi.prepColloquio(id, { periodo })
      setPrepData(res.data?.data || null)
    } catch (error) {
      if (error.response?.status !== 403) {
        showError('Errore nel caricamento prep colloquio')
      }
    } finally {
      setPrepLoading(false)
    }
  }

  // Handler per aggiornamento tags
  const handleTagsChange = async (newTags) => {
    try {
      await minoriApi.update(id, { tags: newTags })
      setMinore(prev => ({ ...prev, tags: newTags }))
    } catch (error) {
      showError('Errore nel salvataggio dei tag')
    }
  }

  // Handler per aggiornamento note
  const handleNotesChange = async (newNotes) => {
    try {
      await minoriApi.update(id, { quickNotes: newNotes })
      setMinore(prev => ({ ...prev, quickNotes: newNotes }))
    } catch (error) {
      showError('Errore nel salvataggio delle note')
    }
  }

  const handleCreatePep = async (pepData) => {
    try {
      await pepApi.create(pepData)
      showSuccess('Progetto educativo creato')
      setShowPepWizard(false)
      await loadPepData()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Errore nella creazione del PEP'
      showError(msg)
      throw error
    }
  }

  const handleAddVerifica = async (verificaData) => {
    if (!pep?._id || !verificaTarget) return
    try {
      await pepApi.addVerifica(pep._id, verificaTarget, verificaData)
      showSuccess('Verifica aggiunta')
      setVerificaTarget(null)
      await loadPepData()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || "Errore nell'aggiunta della verifica"
      showError(msg)
      throw error
    }
  }

  const handleAddNota = async (obId, testo) => {
    if (!pep?._id) return
    try {
      await pepApi.addNota(pep._id, obId, { testo })
      await loadPepData()
    } catch (error) {
      showError("Errore nell'aggiunta della nota")
      throw error
    }
  }

  const handleAddObiettivo = async (pepId, obiettivoData) => {
    try {
      await pepApi.addObiettivo(pepId, obiettivoData)
      showSuccess('Obiettivo aggiunto')
      setShowNuovoObiettivo(false)
      await loadPepData()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || "Errore nell'aggiunta dell'obiettivo"
      showError(msg)
      throw error
    }
  }

  const handleEditObiettivo = async (pepId, obiettivoData) => {
    if (!editObiettivoId) return
    try {
      await pepApi.updateObiettivo(pepId, editObiettivoId, obiettivoData)
      showSuccess('Obiettivo aggiornato')
      setEditObiettivoId(null)
      await loadPepData()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || "Errore nell'aggiornamento dell'obiettivo"
      showError(msg)
      throw error
    }
  }

  const handleAddColloquio = async (colloquioData) => {
    if (!pep?._id) return
    try {
      await pepApi.addColloquio(pep._id, colloquioData)
      showSuccess('Colloquio registrato')
      setShowColloquioForm(false)
      await loadPepData()
    } catch (error) {
      const msg = error.response?.data?.message || error.response?.data?.error || 'Errore nella registrazione del colloquio'
      showError(msg)
      throw error
    }
  }

  if (loading) return <PageLoader />
  if (!minore) return null

  const age = minore.dataNascita ? calcAge(minore.dataNascita) : null
  const hasIndirizzo = minore.indirizzo?.via || minore.indirizzo?.citta
  const hasContatti = minore.contattiFamiglia && minore.contattiFamiglia.length > 0

  // Helper per ottenere contatto per relazione
  const getContatto = (relazione) => minore.contattiFamiglia?.find(c => c.relazione === relazione)
  const padre = getContatto('padre')
  const madre = getContatto('madre')
  const altri = minore.contattiFamiglia?.filter(c => !['padre', 'madre'].includes(c.relazione)) || []

  // Set di entryId già promossi nella timeline PEP
  const promotedEntryIds = new Set(
    (pep?.eventi || [])
      .filter(e => e.entryId)
      .map(e => typeof e.entryId === 'object' ? e.entryId.toString() : e.entryId)
  )

  // Riepilogo entry per pre-compilare il titolo della promozione
  const entryRiepilogo = (entry) => {
    const dateStr = new Date(entry.data).toLocaleDateString('it-IT', { day: 'numeric', month: 'long' })
    const parts = []
    if (entry.segnalazioneUrgente) parts.push('Segnalazione urgente')
    if (entry.comportamentiProblematici?.length) parts.push('Comportamenti: ' + entry.comportamentiProblematici.slice(0, 2).join(', '))
    if (entry.attivitaGruppo?.length) parts.push('Attività: ' + entry.attivitaGruppo.slice(0, 2).join(', '))
    return parts.length ? `${dateStr} — ${parts.join('; ')}` : `Registrazione ${dateStr}`
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Link to="/minori" className="inline-flex items-center text-gray-500 hover:text-gray-700">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Torna ai minori
        </Link>
        <div className="flex space-x-2">
          {isCoordinatore() && (
            <button onClick={() => navigate(`/minori/${id}/edit`)} className="btn-secondary flex items-center">
              <Edit className="h-4 w-4 mr-2" />Modifica
            </button>
          )}
          <Link to={`/entries/new/${id}`} className="btn-primary flex items-center">
            <Plus className="h-4 w-4 mr-2" />Nuova Registrazione
          </Link>
        </div>
      </div>

      {/* Info Card */}
      <Card>
        <div className="flex items-start space-x-4">
          <div className="w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center text-3xl">
            {age && age < 12 ? '🧒' : '👦'}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {minore.nomeCompleto}
            </h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500">
              {age && (
                <span className="flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  {age} anni
                </span>
              )}
              {minore.educatoreReferente && (
                <span className="flex items-center">
                  <User className="h-4 w-4 mr-1" />
                  Ref: {minore.educatoreReferente.nome || minore.educatoreReferente.username}
                </span>
              )}
              {minore.codiceFiscale && (
                <span>CF: {minore.codiceFiscale}</span>
              )}
            </div>

            {/* Telefono e Indirizzo */}
            {(minore.telefono || hasIndirizzo) && (
              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                {minore.telefono && (
                  <a href={`tel:${minore.telefono}`} className="flex items-center text-primary-600 hover:text-primary-700">
                    <Phone className="h-4 w-4 mr-1" />
                    {minore.telefono}
                  </a>
                )}
                {hasIndirizzo && (
                  <span className="flex items-center text-gray-600 dark:text-gray-400">
                    <MapPin className="h-4 w-4 mr-1" />
                    {[minore.indirizzo.via, minore.indirizzo.citta, minore.indirizzo.cap].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            )}

            {minore.note && (
              <p className="mt-3 text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                {minore.note}
              </p>
            )}

            {/* Sezione Tag */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <QuickTags
                minoreId={minore._id}
                initialTags={minore.tags || []}
                onTagsChange={handleTagsChange}
                editable={isCoordinatore()}
              />
            </div>
          </div>
        </div>
      </Card>

      {/* Card Note Rapide */}
      <Card>
        <div className="flex items-center gap-2 mb-4">
          <StickyNote className="h-5 w-5 text-yellow-500" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Note Rapide
          </h2>
        </div>
        <QuickNotes
          minoreId={minore._id}
          initialNotes={minore.quickNotes || []}
          onNotesChange={handleNotesChange}
        />
      </Card>

      {/* Contatti Familiari */}
      {hasContatti && (
        <Card>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-primary-600" />
            Contatti Familiari
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Padre */}
            {padre && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">👨 Padre</p>
                <p className="font-medium text-gray-900 dark:text-white">{padre.nome || '-'}</p>
                {padre.telefono && (
                  <a href={`tel:${padre.telefono}`} className="text-sm text-primary-600 hover:underline flex items-center mt-1">
                    <Phone className="h-3 w-3 mr-1" />{padre.telefono}
                  </a>
                )}
              </div>
            )}

            {/* Madre */}
            {madre && (
              <div className="p-4 bg-pink-50 dark:bg-pink-900/20 rounded-lg">
                <p className="text-sm text-pink-600 dark:text-pink-400 font-medium mb-1">👩 Madre</p>
                <p className="font-medium text-gray-900 dark:text-white">{madre.nome || '-'}</p>
                {madre.telefono && (
                  <a href={`tel:${madre.telefono}`} className="text-sm text-primary-600 hover:underline flex items-center mt-1">
                    <Phone className="h-3 w-3 mr-1" />{madre.telefono}
                  </a>
                )}
              </div>
            )}

            {/* Altri contatti */}
            {altri.map((contatto, idx) => (
              <div key={idx} className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400 font-medium mb-1">
                  📋 {contatto.note || contatto.relazione || 'Altro'}
                </p>
                <p className="font-medium text-gray-900 dark:text-white">{contatto.nome || '-'}</p>
                {contatto.telefono && (
                  <a href={`tel:${contatto.telefono}`} className="text-sm text-primary-600 hover:underline flex items-center mt-1">
                    <Phone className="h-3 w-3 mr-1" />{contatto.telefono}
                  </a>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="text-center">
          <p className="text-2xl font-bold text-primary-600">{entries.length}</p>
          <p className="text-sm text-gray-500">Registrazioni</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-green-600">
            {entries.filter(e => e.presente === 'Sì' || e.presente === true).length}
          </p>
          <p className="text-sm text-gray-500">Presenze</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-orange-600">
            {entries.filter(e => e.presente === 'No' || e.presente === false).length}
          </p>
          <p className="text-sm text-gray-500">Assenze</p>
        </Card>
        <Card className="text-center">
          <p className="text-2xl font-bold text-red-600">
            {entries.filter(e => e.segnalazioneUrgente).length}
          </p>
          <p className="text-sm text-gray-500">Segnalazioni</p>
        </Card>
      </div>

      {/* Ultime Registrazioni */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            📝 Ultime Registrazioni
          </h2>
          <Link to={`/entries?minore=${id}`} className="text-sm text-primary-600 hover:underline">
            Vedi tutte
          </Link>
        </div>

        {entries.length > 0 ? (
          <div className="divide-y divide-gray-100 dark:divide-gray-700">
            {entries.slice(0, 5).map((entry) => (
              <div key={entry._id} className="py-3 -mx-4 lg:-mx-6 px-4 lg:px-6">
                <Link
                  to={`/entries/${entry._id}/edit`}
                  className="flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-md -mx-2 px-2 py-1"
                >
                  <div className="flex items-center">
                    {entry.presente === 'Sì' || entry.presente === true ? (
                      <CheckCircle className="h-5 w-5 text-green-500 mr-3" />
                    ) : (
                      <Clock className="h-5 w-5 text-orange-500 mr-3" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {new Date(entry.data).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <p className="text-sm text-gray-500">
                        {entry.educatore} {entry.segnalazioneUrgente && <Badge variant="error">Urgente</Badge>}
                      </p>
                    </div>
                  </div>
                  <FileText className="h-5 w-5 text-gray-400" />
                </Link>
                {/* Bottone promozione timeline — solo coordinatore con PEP attivo */}
                {isCoordinatore() && pep?._id && (
                  <div className="mt-1 ml-8">
                    <PromoteEntryButton
                      entryId={entry._id}
                      pepId={pep._id}
                      entryDate={entry.data}
                      entryRiepilogo={entryRiepilogo(entry)}
                      isAlreadyPromoted={promotedEntryIds.has(entry._id)}
                      onPromoted={loadPepData}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-gray-500 py-8">
            Nessuna registrazione ancora
          </p>
        )}
      </Card>

      {/* PEP Wizard Modal */}
      {showPepWizard && (
        <PepWizard
          minoreId={id}
          minoreNome={minore.nomeCompleto}
          onConfirm={handleCreatePep}
          onClose={() => setShowPepWizard(false)}
        />
      )}

      {/* Export PDF Modal */}
      {showExportModal && pep && (
        <PepExportModal
          pepId={pep._id}
          pepStato={pep.stato}
          onClose={() => setShowExportModal(false)}
        />
      )}

      {/* Chiusura PEP Modal */}
      {showChiusuraModal && pep && (
        <PepChiusuraModal
          pep={pep}
          minoreNome={minore.nomeCompleto}
          onClose={() => setShowChiusuraModal(false)}
          onDone={loadPepData}
        />
      )}

      {/* Verifica Form Modal */}
      {verificaTarget && pep && (() => {
        const ob = pep.obiettivi?.find(o => o._id === verificaTarget)
        if (!ob) return null
        return (
          <VerificaForm
            obiettivo={ob}
            pepId={pep._id}
            onConfirm={handleAddVerifica}
            onClose={() => setVerificaTarget(null)}
          />
        )
      })()}

      {/* Import Rapido Modal */}
      {showImportRapido && (
        <PepImportRapido
          onClose={() => setShowImportRapido(false)}
          onDone={loadPepData}
        />
      )}

      {/* Colloquio Form Modal */}
      {showColloquioForm && pep && (
        <ColloquioForm
          obiettiviAttivi={(pep.obiettivi || []).filter(o => o.stato === 'attivo')}
          onConfirm={handleAddColloquio}
          onClose={() => setShowColloquioForm(false)}
        />
      )}

      {/* Nuovo Obiettivo Form Modal */}
      {showNuovoObiettivo && pep && (
        <NuovoObiettivoForm
          pepId={pep._id}
          onConfirm={handleAddObiettivo}
          onClose={() => setShowNuovoObiettivo(false)}
        />
      )}

      {/* Edit Obiettivo Form Modal */}
      {editObiettivoId && pep && (() => {
        const ob = pep.obiettivi?.find(o => o._id === editObiettivoId)
        if (!ob) return null
        return (
          <NuovoObiettivoForm
            pepId={pep._id}
            obiettivo={ob}
            onConfirm={handleEditObiettivo}
            onClose={() => setEditObiettivoId(null)}
          />
        )
      })()}

      {/* ── Prep Colloquio standalone (quando PEP non è abilitato) ── */}
      {!pepEnabled && prepColloquio.visible && isCoordinatore() && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <ClipboardList className="h-5 w-5 mr-2 text-indigo-500" />
              <h3 className="font-semibold text-gray-900 dark:text-white">Prep Colloquio</h3>
            </div>
            <div className="flex items-center gap-2">
              {[7, 30, 90].map(p => (
                <button
                  key={p}
                  onClick={() => {
                    setPrepPeriodo(p)
                    loadPrepColloquio(p)
                  }}
                  className={`px-2 py-1 text-xs rounded ${
                    prepPeriodo === p
                      ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                      : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {p}g
                </button>
              ))}
            </div>
          </div>
          {!prepData && !prepLoading && (
            <button
              onClick={() => loadPrepColloquio()}
              className="btn-primary text-sm"
            >
              Genera report
            </button>
          )}
          {(prepData || prepLoading) && (
            <PrepColloquioContent
              data={prepData}
              loading={prepLoading}
              periodo={prepPeriodo}
              onChangePeriodo={(p) => {
                setPrepPeriodo(p)
                loadPrepColloquio(p)
              }}
            />
          )}
        </Card>
      )}

      {/* ── PEP: Progetto Educativo + Timeline ────────────────── */}
      {pepEnabled && (
        <ExperimentalWrapper isExperimental={true}>
          {/* Tab switcher */}
          <div className="flex items-center border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            <button
              onClick={() => setActiveTab('progetto')}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'progetto'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Target className="h-4 w-4 flex-shrink-0" />
              <span className="hidden sm:inline">Progetto Educativo</span>
              <span className="sm:hidden">Progetto</span>
            </button>
            <button
              onClick={() => setActiveTab('timeline')}
              className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'timeline'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <GitBranch className="h-4 w-4 flex-shrink-0" />
              Timeline
            </button>
            {prepColloquio.visible && isCoordinatore() && (
              <button
                onClick={() => {
                  setActiveTab('prep-colloquio')
                  if (!prepData) loadPrepColloquio()
                }}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-3 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === 'prep-colloquio'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <ClipboardList className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">Prep Colloquio</span>
                <span className="sm:hidden">Colloquio</span>
              </button>
            )}
            {/* Export PDF button */}
            {isCoordinatore() && pep && (
              <button
                onClick={() => setShowExportModal(true)}
                className="ml-auto mr-2 sm:mr-4 flex items-center gap-1.5 text-xs text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors whitespace-nowrap"
                title="Esporta PDF"
              >
                <Download className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">PDF</span>
              </button>
            )}
          </div>

          {/* Tab content */}
          <div className="p-3 sm:p-4 lg:p-6">
            {pepLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
              </div>
            ) : activeTab === 'progetto' ? (
              <PepTab
                pep={pep}
                isCoord={isCoordinatore()}
                isEducatore={!isCoordinatore()}
                onCreatePep={() => setShowPepWizard(true)}
                onSelectObiettivo={(obId) => {
                  if (obId === 'new') setShowNuovoObiettivo(true)
                }}
                onAddVerifica={(obId) => setVerificaTarget(obId)}
                onEditObiettivo={(obId) => setEditObiettivoId(obId)}
                onAddNota={handleAddNota}
                onAddColloquio={() => setShowColloquioForm(true)}
                onImportRapido={() => setShowImportRapido(true)}
                onChiudiPep={() => setShowChiusuraModal(true)}
                onReload={loadPepData}
              />
            ) : activeTab === 'prep-colloquio' ? (
              <PrepColloquioContent
                data={prepData}
                loading={prepLoading}
                periodo={prepPeriodo}
                onChangePeriodo={(p) => {
                  setPrepPeriodo(p)
                  loadPrepColloquio(p)
                }}
              />
            ) : (
              <TimelineTab
                timeline={timeline}
                isCoord={isCoordinatore()}
              />
            )}
          </div>
        </ExperimentalWrapper>
      )}
    </div>
  )
}

// Componente Prep Colloquio
function PrepColloquioContent({ data, loading, periodo, onChangePeriodo }) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
      </div>
    )
  }

  if (!data) {
    return (
      <p className="text-gray-500 dark:text-gray-400 text-center py-8">
        Nessun dato disponibile
      </p>
    )
  }

  const { kpi, topComportamenti, accompagnatori, segnalazioni, puntiForza, puntiAttenzione, pep } = data

  return (
    <div className="space-y-4">
      {/* Periodo selector */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {data.periodo?.entriesAnalizzate || 0} registrazioni analizzate
        </p>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map(p => (
            <button
              key={p}
              onClick={() => onChangePeriodo(p)}
              className={`px-2.5 py-1 text-xs rounded-lg font-medium transition-colors ${
                periodo === p
                  ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300'
                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {p}g
            </button>
          ))}
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">{kpi.presenzePerc}%</p>
          <p className="text-xs text-blue-600 dark:text-blue-400">Presenze</p>
          <p className="text-xs text-blue-500">{kpi.presenti}/{kpi.presenti + kpi.assenti}</p>
        </div>
        <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-2xl font-bold text-green-700 dark:text-green-300">{kpi.compitiPerc}%</p>
          <p className="text-xs text-green-600 dark:text-green-400">Compiti</p>
        </div>
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <p className="text-2xl font-bold text-amber-700 dark:text-amber-300">{kpi.episodiComportamento}</p>
          <p className="text-xs text-amber-600 dark:text-amber-400">Ep. comportamento</p>
        </div>
        <div className={`p-3 rounded-lg border ${
          kpi.trendPresenze > 0
            ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
            : kpi.trendPresenze < 0
              ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
          <p className={`text-2xl font-bold ${
            kpi.trendPresenze > 0 ? 'text-emerald-700 dark:text-emerald-300' :
            kpi.trendPresenze < 0 ? 'text-red-700 dark:text-red-300' :
            'text-gray-700 dark:text-gray-300'
          }`}>
            {kpi.trendPresenze > 0 ? '+' : ''}{kpi.trendPresenze}
          </p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Trend presenze</p>
        </div>
      </div>

      {/* Punti di forza e attenzione */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {puntiForza.length > 0 && (
          <div className="p-3 rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800">
            <h4 className="text-sm font-semibold text-green-800 dark:text-green-300 mb-2">Punti di forza</h4>
            <ul className="space-y-1">
              {puntiForza.map((p, i) => (
                <li key={i} className="text-sm text-green-700 dark:text-green-400 flex items-start">
                  <CheckCircle className="h-4 w-4 mr-1.5 mt-0.5 flex-shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}
        {puntiAttenzione.length > 0 && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800">
            <h4 className="text-sm font-semibold text-amber-800 dark:text-amber-300 mb-2">Punti di attenzione</h4>
            <ul className="space-y-1">
              {puntiAttenzione.map((p, i) => (
                <li key={i} className="text-sm text-amber-700 dark:text-amber-400 flex items-start">
                  <span className="mr-1.5">!</span>
                  {p}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Top comportamenti */}
      {topComportamenti.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Comportamenti frequenti</h4>
          <div className="space-y-1.5">
            {topComportamenti.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">{c.label}</span>
                <span className="text-gray-500 dark:text-gray-400 font-medium">{c.count}x</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Accompagnatori */}
      {accompagnatori.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Accompagnatori</h4>
          <div className="flex flex-wrap gap-2">
            {accompagnatori.map((a, i) => (
              <span key={i} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-700 dark:text-gray-300">
                {a.label} ({a.count})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* PEP obiettivi */}
      {pep && pep.obiettivi?.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">Obiettivi PEP</h4>
          <div className="space-y-2">
            {pep.obiettivi.map((o, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-sm text-gray-700 dark:text-gray-300">{o.titolo}</p>
                  <p className="text-xs text-gray-500">{o.area} - {o.stato}</p>
                </div>
                <div className="w-20">
                  <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${o.progressoPercentuale}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-right mt-0.5">{o.progressoPercentuale}%</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Segnalazioni */}
      {segnalazioni.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-2">
            Segnalazioni urgenti ({segnalazioni.length})
          </h4>
          <div className="space-y-1.5">
            {segnalazioni.slice(0, 5).map((s, i) => (
              <div key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
                <span className="text-xs text-gray-500 mr-2 mt-0.5 flex-shrink-0">
                  {new Date(s.data).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                </span>
                {s.testo}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bottone stampa */}
      <div className="flex justify-end pt-2">
        <button
          onClick={() => window.print()}
          className="text-sm text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          Stampa
        </button>
      </div>
    </div>
  )
}

function calcAge(d) {
  const t = new Date(), b = new Date(d)
  let a = t.getFullYear() - b.getFullYear()
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a--
  return a
}
